import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, useWindowDimensions, Platform } from 'react-native';
import { useUnistyles, StyleSheet } from 'react-native-unistyles';
import { quickReactions } from '@/utils/ReactionHelpers';
import Animated, { useAnimatedStyle, useSharedValue, withSpring, withTiming, withDelay } from 'react-native-reanimated';
import { impactLight, impactMedium } from '@/utils/haptic';
import { BlurView } from 'expo-blur';
import Clipboard from '@react-native-clipboard/clipboard';
import EmojiPicker, { emojisByCategory } from 'rn-emoji-keyboard';
import { excludedEmojis } from '@/utils/constants/excludedEmojis';
import Unicon from '@/components/General/Unicon';
import useMessagesStore from '@/data/store/useMessagesStore';

const SPRING = { damping: 22, stiffness: 280, mass: 0.8 };

let filteredEmojisByCategory;

const getFilteredEmojisByCategory = () => {
    if (!filteredEmojisByCategory) {
        const excludedEmojiNames = new Set(excludedEmojis);
        filteredEmojisByCategory = emojisByCategory.map(cat => ({
            ...cat,
            data: cat.data.filter(emoji => !excludedEmojiNames.has(emoji.name)),
        }));
    }

    return filteredEmojisByCategory;
};

/**
 * ReactionMenu — modern floating context menu for chat messages.
 *
 * Props:
 *   message: the message object
 *   triggerRef: ref to the bubble view (for measuring position)
 *   isOpen: boolean
 *   isCurrentUser: boolean
 *   canCopy: boolean
 *   onReactionPress: (message, reactionName) => void
 *   onReply: (message) => void
 *   onDelete: (message) => void
 *   onEdit: (message) => void  (no functionality yet — just calls back)
 *   onClose: () => void
 */
const ReactionMenu = ({
    message,
    triggerRef,
    isOpen,
    isCurrentUser = false,
    canReact = true,
    canReply = true,
    canCopy = true,
    canDelete = false,
    onReactionPress = () => {},
    onReply = () => {},
    onDelete = () => {},
    onEdit = () => {},
    onClose = () => {},
}) => {
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0, w: 0, h: 0, ready: false });
    const [copyClicked, setCopyClicked] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const closeTimeoutRef = useRef(null);
    const measurementRequestRef = useRef(0);

    const { theme: { colors, scheme } } = useUnistyles();
    const { height: screenHeight, width: screenWidth } = useWindowDimensions();

    //Animation values
    const backdropOpacity = useSharedValue(0);
    const menuScale = useSharedValue(0);
    const menuTranslateY = useSharedValue(20);
    const actionsOpacity = useSharedValue(0);
    const actionsTranslateY = useSharedValue(10);

    useEffect(() => {
        if (isOpen) {
            open();
        } else {
            close();
        }

        if (Platform.OS !== 'web') return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                close();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen]);

    const positionMenu = (px, py, w, h) => {
        //Position the menu below the bubble, clamped to screen edges
        const menuWidth = 240;
        let x = px;
        if (isCurrentUser) {
            x = Math.min(px + w - menuWidth, screenWidth - 16);
        } else {
            x = Math.max(px, 16);
        }
        x = Math.max(16, Math.min(x, screenWidth - menuWidth - 16));

        //If not enough space below (account for tab bar ~90px), show above
        const menuEstHeight = 200;
        const bottomThreshold = screenHeight - 120;
        let y = py + h + 8;
        if (y + menuEstHeight > bottomThreshold) {
            y = py - menuEstHeight - 8;
        }
        y = Math.max(60, y);

        setMenuPos({ x, y, w, h, ready: true });
    };

    useEffect(() => {
        if (!isOpen || !menuPos.ready) return;

        backdropOpacity.value = withTiming(1, { duration: 150 });
        menuScale.value = withSpring(1, SPRING);
        menuTranslateY.value = withSpring(0, SPRING);
        actionsOpacity.value = withDelay(80, withTiming(1, { duration: 150 }));
        actionsTranslateY.value = withDelay(80, withSpring(0, SPRING));
    }, [isOpen, menuPos.ready]);

    const open = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }

        const requestId = ++measurementRequestRef.current;
        const applyPosition = (x, y, w, h) => {
            if (measurementRequestRef.current === requestId) {
                positionMenu(x, y, w, h);
            }
        };
        const applyFallbackPosition = () => {
            applyPosition(isCurrentUser ? screenWidth - 16 : 16, 60, 0, 0);
        };

        applyFallbackPosition();
        backdropOpacity.value = 0;
        menuScale.value = 0;
        menuTranslateY.value = 20;
        actionsOpacity.value = 0;
        actionsTranslateY.value = 10;
        impactLight();

        const node = triggerRef?.current;
        if (Platform.OS === 'web') {
            if (node && typeof node.getBoundingClientRect === 'function') {
                const rect = node.getBoundingClientRect();
                applyPosition(rect.left, rect.top, rect.width, rect.height);
            } else if (typeof node?.measureInWindow === 'function') {
                node.measureInWindow(applyPosition);
            } else {
                applyFallbackPosition();
            }
        } else if (typeof node?.measureInWindow === 'function') {
            node.measureInWindow(applyPosition);
        } else if (typeof node?.measure === 'function') {
            node.measure((fx, fy, w, h, px, py) => {
                applyPosition(px, py, w, h);
            });
        } else {
            applyFallbackPosition();
        }
    };

    const close = () => {
        measurementRequestRef.current += 1;
        backdropOpacity.value = withTiming(0, { duration: 150 });
        menuScale.value = withTiming(0, { duration: 150 });
        menuTranslateY.value = withTiming(10, { duration: 150 });
        actionsOpacity.value = withTiming(0, { duration: 100 });
        actionsTranslateY.value = withTiming(10, { duration: 100 });
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(() => {
            setMenuPos(prev => prev.ready ? { ...prev, ready: false } : prev);
            if (isOpen) onClose();
        }, 200);
    };

    const handleReaction = (name) => {
        impactLight();
        onReactionPress(message, name);
        close();
    };

    const handleMoreEmojis = () => {
        setShowEmojiPicker(true);
        //Don't close — the emoji picker overlays, and we close when it closes
    };

    const handleReply = () => {
        impactLight();
        onReply(message);
        close();
    };

    const handleCopy = () => {
        if (message?.text) {
            Clipboard.setString(message.text);
            impactLight();
            setCopyClicked(true);
            setTimeout(() => setCopyClicked(false), 2000);
        }
    };

    const handleDelete = () => {
        impactMedium();
        onDelete(message);
        close();
    };

    const handleEdit = () => {
        impactLight();
        onEdit(message);
        close();
    };

    const backdropStyle = useAnimatedStyle(() => ({
        opacity: backdropOpacity.value,
    }));

    const menuContainerStyle = useAnimatedStyle(() => ({
        transform: [
            { scale: menuScale.value },
            { translateY: menuTranslateY.value },
        ],
        opacity: menuScale.value > 0 ? 1 : 0,
    }));

    const actionsStyle = useAnimatedStyle(() => ({
        opacity: actionsOpacity.value,
        transform: [{ translateY: actionsTranslateY.value }],
    }));

    const quickReactionEntries = Object.entries(quickReactions);

    const hasMenuOptions = (canReply && message?.id) || (canCopy && !!message?.text) || isCurrentUser || canDelete;

    if (!isOpen && !menuPos.ready) return null;

    return (
        <>
            {/* Backdrop */}
            <Animated.View style={[styles.backdrop, backdropStyle]} pointerEvents={isOpen ? 'auto' : 'none'}>
                <BlurView tint={scheme} intensity={40} style={styles.blurView} />
                <Pressable style={styles.backdropPressable} onPress={() => close()} />
            </Animated.View>

            {/* Floating menu */}
            {menuPos.ready && (
                <Animated.View style={[styles.menu, menuContainerStyle, { left: menuPos.x, top: menuPos.y }]}>
                    {/* Quick reactions row */}
                    {canReact && (
                    <View style={[styles.reactionsRow, hasMenuOptions && { borderBottomWidth: 0.5, borderBottomColor: colors.border }]}>
                        {quickReactionEntries.map(([name, emoji]) => (
                            <Pressable
                                key={name}
                                testID="reaction-emoji"
                                style={styles.quickReactionButton}
                                onPress={() => handleReaction(name)}
                            >
                                <Text style={styles.quickReactionEmoji}>{emoji}</Text>
                            </Pressable>
                        ))}
                        {/* More emojis button */}
                        <Pressable style={styles.moreEmojiButton} onPress={handleMoreEmojis}>
                            <Unicon name="add-circle" size={22} colorVariant="primary" />
                        </Pressable>
                    </View>
                    )}

                    {/* Action buttons */}
                    <Animated.View style={[styles.actionsContainer, actionsStyle]}>
                        {canReply && message?.id && (
                            <Pressable style={styles.actionButton} onPress={handleReply}>
                                <Unicon name="arrow-redo" size={18} colorVariant="text" />
                                <Text style={styles.actionText}>Reply</Text>
                            </Pressable>
                        )}
                        {canCopy && message?.text && (
                            <Pressable style={styles.actionButton} onPress={handleCopy}>
                                <Unicon name={copyClicked ? "checkmark" : "copy"} size={18} colorVariant={copyClicked ? "success" : "text"} />
                                <Text style={[styles.actionText, copyClicked && styles.actionTextSuccess]}>
                                    {copyClicked ? "Copied!" : "Copy"}
                                </Text>
                            </Pressable>
                        )}
                        {isCurrentUser && (
                            <Pressable style={styles.actionButton} onPress={handleEdit}>
                                <Unicon name="create-outline" size={18} colorVariant="text" />
                                <Text style={styles.actionText}>Edit</Text>
                            </Pressable>
                        )}
                        {(isCurrentUser || canDelete) && (
                            <Pressable style={[styles.actionButton, styles.actionButtonDelete]} onPress={handleDelete}>
                                <Unicon name="trash" size={18} colorVariant="error" />
                                <Text style={styles.actionTextDelete}>Delete</Text>
                            </Pressable>
                        )}
                    </Animated.View>
                </Animated.View>
            )}

            {/* Full emoji keyboard */}
            {showEmojiPicker && (
                <EmojiPicker
                    onEmojiSelected={(emoji) => {
                        setShowEmojiPicker(false);
                        onReactionPress(message, emoji.name);
                        close();
                    }}
                    open
                    emojisByCategory={getFilteredEmojisByCategory()}
                    onClose={() => setShowEmojiPicker(false)}
                    enableSearchBar
                    categoryPosition="bottom"
                    enableRecentlyUsed
                    categoryOrder={["recently_used"]}
                    styles={{
                        searchBar: {
                            container: {
                                borderRadius: 8,
                            },
                        },
                    }}
                    theme={{
                        backdrop: '#16161888',
                        knob: colors.onSurface,
                        container: colors.surface,
                        header: colors.text,
                        skinTonesContainer: colors.background,
                        category: {
                            icon: colors.primary,
                            iconActive: colors.surface,
                            container: colors.surface,
                            containerActive: colors.primary,
                        },
                        search: {
                            background: colors.background,
                            text: colors.text,
                            placeholder: colors.textSecondary,
                            icon: colors.textSecondary,
                        },
                    }}
                />
            )}
        </>
    );
};

const ConnectedReactionMenuComponent = ({
    selectionType = 'post',
    focused = true,
    canCopy,
    onReactionPress = () => {},
    onReply = () => {},
    onDelete = () => {},
    onEdit = () => {},
    onClose,
    ...props
}) => {
    const selection = useMessagesStore(state => state.messageForReaction);
    const setMessageForReaction = useMessagesStore(state => state.setMessageForReaction);

    const message = selectionType === 'message'
        ? selection?.message
        : selectionType === 'post-or-comment'
            ? selection?.comment ?? selection?.post
            : selection?.post;
    const triggerRef = selectionType === 'message' ? selection?.bubble_ref : selection?.reactionRef;
    const isCurrentUser = selectionType === 'message' ? selection?.isCurrentUser : props.isCurrentUser;
    const resolvedCanCopy = typeof canCopy === 'function' ? canCopy(selection) : canCopy;

    return (
        <ReactionMenu
            {...props}
            message={message}
            triggerRef={triggerRef}
            isOpen={!!selection?.contextMenuOpen && focused}
            isCurrentUser={isCurrentUser}
            canCopy={resolvedCanCopy}
            onReactionPress={(target, name) => onReactionPress(target, name, selection)}
            onReply={(target) => onReply(target, selection)}
            onDelete={(target) => onDelete(target, selection)}
            onEdit={(target) => onEdit(target, selection)}
            onClose={() => {
                setMessageForReaction(current => ({
                    ...(current || {}),
                    contextMenuOpen: false,
                }));
                onClose?.(selection);
            }}
        />
    );
};

export const ConnectedReactionMenu = React.memo(ConnectedReactionMenuComponent);

const styles = StyleSheet.create((theme) => ({
    backdrop: {
        position: Platform.OS === 'web' ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 90,
    },
    blurView: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    backdropPressable: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    menu: {
        position: Platform.OS === 'web' ? 'fixed' : 'absolute',
        width: 240,
        borderRadius: 16,
        backgroundColor: theme.colors.surface,
        overflow: 'hidden',
        zIndex: 100,
        ...(Platform.OS !== 'web' ? {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.3,
            shadowRadius: 16,
            elevation: 10,
        } : {
            boxShadow: theme.shadows.elevated,
        }),
    },
    reactionsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        paddingVertical: 6,
        paddingHorizontal: 4,
    },
    quickReactionButton: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 18,
    },
    quickReactionEmoji: {
        fontSize: 22,
    },
    moreEmojiButton: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 18,
    },
    actionsContainer: {
        paddingVertical: 4,
    },
    actionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 11,
        paddingHorizontal: 16,
        gap: 12,
    },
    actionButtonDelete: {
        //No special style needed — text/icon are already error-colored
    },
    actionText: {
        color: theme.colors.text,
        fontSize: 14,
        fontFamily: theme.fonts.heading,
    },
    actionTextSuccess: {
        color: theme.colors.success,
    },
    actionTextDelete: {
        color: theme.colors.error,
        fontSize: 14,
        fontFamily: theme.fonts.heading,
    },
}));

export default React.memo(ReactionMenu);
